// src/oura/screens/TodayList.tsx
//
// Today: every client's next step that needs us, in one list, sorted by the
// same rule every screen uses (a fix that came back, a client reply waiting
// on us, the item due soonest, then the plan's order). A row opens that
// client's Delivery with the deliverable's drawer open.

import { useEffect, useMemo, useRef, useState } from "react";
import { SunIcon, UserPlusIcon } from "lucide-react";
import type { Onboarding } from "@service/aeo/onboarding";
import { nextAction, PRESS_NEXT, PRESS_PARAM, type NextAction, type OnboardingPlanFacts, type PlanRead } from "../onboarding/derive";
import type { TodayPayload, TodayRow } from "@service/aeo/delivery";
import { actorText, getToday, isDeliveryServed, subscribeDelivery } from "@service/aeo/delivery";
import { OURA_ROOT, ouraHref } from "../nav";
import { cn } from "../lib/cn";
import { FamilyChip, FoldCard, FoldScope, GroupFold, ListRow, TYPE } from "../ui";
import { familyOf } from "../components/plan/planModel";

type Load = { state: "loading" } | { state: "failed" } | { state: "ready"; data: TodayPayload };

function rowHref(r: TodayRow): string {
    return `${OURA_ROOT}/businesses/${encodeURIComponent(r.businessId)}/delivery?d=${encodeURIComponent(r.deliverableKey)}`;
}

function Row({ r }: { r: TodayRow }) {
    const who = actorText(r.actor);
    const warn = r.actor.late || r.actor.cameBack;
    return (
        <ListRow
            lead="square"
            icon={<FamilyChip family={familyOf(r.code)}>{r.code}</FamilyChip>}
            title={`${r.businessName} · ${r.label}`}
            sub={<span className="text-gray-900">{r.next.text}</span>}
            meta={warn ? undefined : who}
            late={warn ? who : undefined}
            to={rowHref(r)}
            stackMeta
            data-today={`${r.businessId}:${r.deliverableKey}`}
        />
    );
}

/** An onboarding client whose Next card is ours: the same words the card says. */
interface OnboardingDue {
    businessId: string;
    name: string;
    next: NextAction;
}

/**
 * The onboarding Next cards that need us, most urgent first (late, then the
 * order the cards themselves come in). Waiting on the client or on Oura is not here.
 */
function onboardingDue(records: Record<string, Onboarding>, plans: Record<string, OnboardingPlanFacts>, now: number, planReads: Record<string, PlanRead | undefined> = {}): OnboardingDue[] {
    return Object.values(records)
        .map((o) => ({ businessId: o.businessId, name: o.businessName || o.domain, next: nextAction(o, now, { plan: plans[o.businessId] ?? null, planRead: planReads[o.businessId] }) }))
        // A plan not read yet stays on the list ("Reading the plan"), never dropped as if complete (Jack, #4284).
        .filter((r) => (r.next.who === "staff" && r.next.cta && r.next.kind !== "done") || r.next.kind === "read_plan")
        .sort((a, b) => Number(b.next.late) - Number(a.next.late));
}

function OnboardingRow({ r }: { r: OnboardingDue }) {
    return (
        <ListRow
            lead="square"
            icon={<UserPlusIcon aria-hidden size={16} className="text-blue-600" />}
            title={`${r.name} · ${r.next.title}`}
            sub={<span className="text-gray-900">{r.next.why}</span>}
            meta={r.next.late ? undefined : r.next.cta ?? undefined}
            late={r.next.late ? r.next.cta ?? "Late" : undefined}
            // The row's press runs on arrival, as from the Overview card (opensOnArrival decides which may);
            // a plan still being read has no press to carry (Jack, #4287).
            to={`${ouraHref("onboarding", { role: "oura-staff", businessId: r.businessId })}${r.next.cta ? `?${PRESS_PARAM}=${PRESS_NEXT}` : ""}`}
            stackMeta
            data-today={`${r.businessId}:onboarding`}
        />
    );
}

export interface TodayListProps {
    /** The clients being onboarded, as the list read them: their Next cards that need us lead the list. */
    onboarding?: { records: Record<string, Onboarding>; plans: Record<string, OnboardingPlanFacts>; planReads?: Record<string, PlanRead | undefined> };
}

export function TodayList({ onboarding }: TodayListProps = {}) {
    // A clock that ticks each minute: a deadline that passes while Today stays open turns the row late (Jack, #4285).
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 60_000);
        return () => window.clearInterval(timer);
    }, []);
    const due = useMemo(() => (onboarding ? onboardingDue(onboarding.records, onboarding.plans, now, onboarding.planReads) : []), [onboarding, now]);
    // Without Delivery there is nothing to read: an empty, finished Delivery part, so onboarding rows
    // still show instead of the skeleton forever (Jack, #4289).
    const [load, setLoad] = useState<Load>(() => (isDeliveryServed ? { state: "loading" } : { state: "ready", data: { asOf: new Date().toISOString(), rows: [], notSet: [] } }));
    /** Every Delivery write reads the list again; only the newest read lands, so a slow older one never brings back done work. */
    const readSeq = useRef(0);

    useEffect(() => {
        if (!isDeliveryServed) return;
        let live = true;
        const read = () => {
            const mine = ++readSeq.current;
            return getToday().then((r) => {
                if (!live || mine !== readSeq.current) return;
                setLoad(r.ok ? { state: "ready", data: r.data } : { state: "failed" });
            });
        };
        void read();
        const off = subscribeDelivery(() => void read());
        return () => {
            live = false;
            off();
        };
    }, []);

    if (!isDeliveryServed && due.length === 0) return <p className={cn(TYPE.tb, "text-gray-600")}>The Today list comes with Delivery, which is not available here yet.</p>;
    // Delivery's loading and failure hold only Delivery's part: onboarding rows show regardless (Jack, #4296).
    if (load.state === "loading" && due.length === 0) return <div className="h-48 animate-pulse rounded-2xl bg-white" aria-busy />;
    if (load.state === "failed" && due.length === 0) return <p className={cn(TYPE.tb, "text-gray-600")}>The Today list did not load. Try again in a minute.</p>;

    const rows = load.state === "ready" ? load.data.rows ?? [] : [];
    const notSet = load.state === "ready" ? load.data.notSet ?? [] : [];
    const dueClients = new Set(due.map((r) => r.businessId));
    // The band the server sends (came back, a client reply, late, or due by today); without one, what the actor shows.
    const bandOf = (r: TodayRow) => r.band ?? (r.actor.cameBack || r.actor.late ? "today" : "coming");
    const today = rows.filter((r) => bandOf(r) === "today");
    const coming = rows.filter((r) => bandOf(r) === "coming");
    const clients = new Set([...rows.map((r) => r.businessId), ...Array.from(dueClients)]).size;
    // A plan still being read is listed, but it is not a step that needs us yet.
    const dueUs = due.filter((r) => r.next.kind !== "read_plan");
    const needUs = today.length + dueUs.length;

    return (
        <FoldScope value="staff-today">
            <p className={cn(TYPE.ts, "m-0 mb-4")}>
                {/* Delivery failed: its steps are not counted, so no total claims to be the whole day (Jack, #4288). */}
                {load.state === "failed"
                    ? `${dueUs.length} onboarding ${dueUs.length === 1 ? "step needs" : "steps need"} us today. Delivery could not be counted.`
                    : rows.length === 0 && due.length === 0
                      ? "Nothing needs us today."
                      : `${needUs} ${needUs === 1 ? "step needs" : "steps need"} us today, across ${clients} ${clients === 1 ? "client" : "clients"}. ${coming.length} more coming up.`}
            </p>
            <FoldCard
                id="today"
                title="Today"
                icon={{ icon: SunIcon, area: "work" }}
                foldedFact={load.state === "failed" ? `${dueUs.length} onboarding · Delivery unavailable` : `${needUs} today · ${coming.length} coming up`}
            >
                <div>
                    {due.length > 0 && (
                        <GroupFold id="today.onboarding" tone="blue" title="Onboarding" count={due.length} lead="square">
                            {due.map((r) => (
                                <OnboardingRow key={r.businessId} r={r} />
                            ))}
                        </GroupFold>
                    )}
                    {today.length > 0 && (
                        <GroupFold id="today.now" tone="blue" title="Delivery" count={today.length} lead="square">
                            {today.map((r) => (
                                <Row key={`${r.businessId}:${r.deliverableKey}`} r={r} />
                            ))}
                        </GroupFold>
                    )}
                    {coming.length > 0 && (
                        <GroupFold id="today.coming" tone="gray" title="Coming up" count={coming.length} lead="square">
                            {coming.map((r) => (
                                <Row key={`${r.businessId}:${r.deliverableKey}`} r={r} />
                            ))}
                        </GroupFold>
                    )}
                    {rows.length === 0 && due.length === 0 && <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Every client's next step is with the client or waiting on Oura's check.</p>}
                    {load.state === "loading" && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")} aria-busy>Reading Delivery…</p>}
                    {load.state === "failed" && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-600")}>Delivery's steps did not load. Try again in a minute.</p>}
                </div>
                {notSet.length > 0 && (
                    <p className={cn(TYPE.tc, "m-0 mt-4 text-gray-500")}>
                        No contract in Oura yet, so no Delivery: {notSet.map((n) => n.businessName).join(", ")}.
                    </p>
                )}
            </FoldCard>
        </FoldScope>
    );
}
